/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Fleet health › Problems (docs/CONTRACT-fleet-governance.md sections 4.5 and 12.2): one row per problem kind in the closed
 * order, from GET /v1/admin/fleet/problems: the kind and its rule in one line, the PCs carrying it now, 7 days of it, the
 * five most recently seen PCs (each opens the PC drawer) and Show PCs, the PCs view filtered to the kind.
 */
import { CircleCheck } from 'lucide-react';
import {
  FLEET_PROBLEM_KINDS, PROBLEM_LABELS, PROBLEM_RULES, ageText, fleetApi, fleetExportHref, fleetFailure, sourcesFailedText,
  type FleetProblemKind, type FleetProblemRow,
} from '../../lib/fleet';
import { Button, EmptyState, PageHeader, Panel, Sparkline, Stack, Table, type Column } from '../../ui';
import { count } from '../../ui/format';
import { useLoad } from '../pulse/common';
import { ExportLink, LoadError, PageLink, SourcesNote, type FleetFrame } from './parts';

/** The rows in the closed order, whatever order the answer used. */
function ordered(items: readonly FleetProblemRow[]): FleetProblemRow[] {
  const byKind = new Map(items.map((row) => [row.kind, row]));
  return FLEET_PROBLEM_KINDS.map((kind: FleetProblemKind) => byKind.get(kind) ?? { kind, pcs: 0, spark: [], recent: [] });
}

export function FleetProblems({ frame }: { frame: FleetFrame }) {
  const { load, reload, reloading } = useLoad(() => fleetApi.problems(), 'problems');
  const data = load.state === 'ready' ? load.data : null;

  const columns: Array<Column<FleetProblemRow>> = [
    {
      key: 'kind', header: 'Problem kind',
      cell: (row) => <span className="ui-cell-stack"><span className="ui-cell-title">{PROBLEM_LABELS[row.kind] ?? row.kind}</span><span className="ui-cell-sub">{PROBLEM_RULES[row.kind] ?? ''}</span></span>,
    },
    { key: 'pcs', header: 'PCs now', numeric: true, cell: (row) => (row.pcs === 0 ? <span className="ui-quiet">0</span> : count(row.pcs)) },
    { key: 'spark', header: '7 days', hideOnPhone: true, cell: (row) => <Sparkline values={row.spark} label={`${PROBLEM_LABELS[row.kind] ?? row.kind}: PCs per day, last ${row.spark.length} days`} tone={row.pcs > 0 ? 'warn' : 'data'} /> },
    {
      key: 'recent', header: 'Most recently seen', hideOnPhone: true,
      cell: (row) => (row.recent.length ? (
        <span className="ui-cell-stack">
          {row.recent.slice(0, 5).map((pc) => (
            <Button key={pc.deviceId} variant="ghost" size="sm" title={`${pc.deviceId} · last seen ${ageText(pc.lastSeenAt)}`} onClick={() => frame.openPc(pc.deviceId)}>{pc.computerName ?? pc.deviceId}</Button>
          ))}
        </span>
      ) : <span className="ui-quiet">—</span>),
    },
    { key: 'show', header: <span className="ui-sr">Show PCs</span>, align: 'end', cell: (row) => <PageLink href={`/fleet?view=pcs&problem=${encodeURIComponent(row.kind)}`}>Show PCs</PageLink> },
  ];

  return (
    <>
      <PageHeader
        tabs={frame.tabs}
        activeTab={frame.activeTab}
        meta={<span>What is going wrong across the fleet, one problem kind a row.</span>}
        actions={(
          <>
            <ExportLink href={fleetExportHref('problems')} />
            {frame.actions}
          </>
        )}
      />
      <div className="ui-page">
        <Stack>
          {load.state === 'failed' ? <LoadError title="The problem kinds could not be loaded" error={load.error} words={(error) => fleetFailure(error)} onRetry={reload} retrying={reloading} /> : null}
          <SourcesNote text={sourcesFailedText(data?.sourcesFailed)} />
          <Panel flush title="Problem kinds" description={data ? `${count(data.items.filter((row) => row.pcs > 0).length)} of ${FLEET_PROBLEM_KINDS.length} kinds on at least one PC` : 'The thirteen problem kinds'}>
            <Table
              label="Problem kinds"
              columns={columns}
              rows={data ? ordered(data.items) : []}
              rowKey={(row) => row.kind}
              loading={load.state === 'loading'}
              skeletonRows={6}
              empty={<EmptyState compact icon={CircleCheck} title="No problem kinds">The thirteen problem kinds appear here.</EmptyState>}
            />
          </Panel>
        </Stack>
      </div>
    </>
  );
}
